import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Input, Modal, Select, Tag, Upload } from "antd";
import { PaperClipOutlined, SendOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Error, Success, Warning } from "../../../commons/Messages/Messages";
import { quillModules } from "../../../utils/quillLayout";
import { mailApi, getErrorMessage } from "./mailApi";
import MessageBody from "./MessageBody";
import {
  escapeHtml,
  formatAddress,
  formatAddressList,
  formatFullDate,
  formatSize,
  htmlToText,
  prefixSubject,
  sameAddress,
  textToHtml
} from "./mailUtils";
import { useMailTranslation } from "./useMailTranslation";

const EMAIL_PATTERN = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

// Tags may be "Name <a@b.de>" or a bare address.
const isValidRecipient = value => {
  const text = String(value ?? "");
  const match = /<([^>]+)>\s*$/.exec(text);
  return EMAIL_PATTERN.test((match ? match[1] : text).trim());
};

const buildInitialState = (context, account, t) => {
  const { mode, message, folder } = context;
  const signature = account?.signature
    ? `<p><br></p><p>--<br>${textToHtml(account.signature)}</p>`
    : "";
  const empty = {
    to: [],
    cc: [],
    bcc: [],
    subject: "",
    body: `<p><br></p>${signature}`,
    quotedHtml: "",
    carried: [],
    source: null,
    inReplyTo: null,
    references: [],
    answeredMessage: null,
    draftUid: null
  };
  if (!message) return empty;

  const originalHtml = message.html || textToHtml(message.text);
  const references = [...(message.references || []), message.messageId].filter(Boolean);
  const carriable = (message.attachments || []).filter(attachment => !attachment.inline);
  const notMe = address => !sameAddress(address.address, account.email);

  if (mode === "draft") {
    return {
      ...empty,
      to: message.to.map(formatAddress),
      cc: message.cc.map(formatAddress),
      bcc: message.bcc.map(formatAddress),
      subject: message.subject,
      body: originalHtml,
      carried: carriable,
      source: { folder, uid: message.uid },
      inReplyTo: message.inReplyTo || null,
      references: message.references || [],
      draftUid: message.uid
    };
  }

  if (mode === "forward") {
    const header = [
      `---------- ${t("Forwarded message")} ----------`,
      `${t("From")}: ${escapeHtml(formatAddressList(message.from))}`,
      `${t("Date")}: ${escapeHtml(formatFullDate(message.date))}`,
      `${t("Subject")}: ${escapeHtml(message.subject)}`,
      `${t("To")}: ${escapeHtml(formatAddressList(message.to))}`,
      message.cc?.length ? `${t("Cc")}: ${escapeHtml(formatAddressList(message.cc))}` : null
    ]
      .filter(Boolean)
      .join("<br>");
    return {
      ...empty,
      subject: prefixSubject("Fwd", message.subject),
      quotedHtml: `<br><div>${header}<br><br>${originalHtml}</div>`,
      carried: carriable,
      source: { folder, uid: message.uid },
      references
    };
  }

  const replyTargets = message.replyTo?.length ? message.replyTo : message.from;
  const to = replyTargets.filter(notMe);
  const cc = [];
  if (mode === "replyAll") {
    const seen = new Set(to.map(address => address.address.toLowerCase()));
    [...message.to, ...message.cc].filter(notMe).forEach(address => {
      if (seen.has(address.address.toLowerCase())) return;
      seen.add(address.address.toLowerCase());
      (to.length ? cc : to).push(address);
    });
  }
  // Replying to your own sent message should go back to its original recipients.
  const finalTo = to.length ? to : message.to;

  const wrote = t("On {{date}}, {{name}} wrote:", {
    date: escapeHtml(formatFullDate(message.date)),
    name: escapeHtml(formatAddressList(message.from))
  });
  // A reply suggested by the mail assistant arrives as plain text.
  const prefill = context.prefillBody
    ? `<p>${textToHtml(context.prefillBody)}</p><p><br></p>${signature}`
    : null;
  return {
    ...empty,
    ...(prefill ? { body: prefill } : {}),
    to: finalTo.map(formatAddress),
    cc: cc.map(formatAddress),
    subject: prefixSubject("Re", message.subject),
    quotedHtml: `<br><div>${wrote}</div><blockquote style="margin:0 0 0 .8ex;border-left:1px solid #ccc;padding-left:1ex">${originalHtml}</blockquote>`,
    inReplyTo: message.messageId || null,
    references,
    answeredMessage: { folder, uid: message.uid }
  };
};

const RecipientSelect = ({ value, onChange, options, placeholder }) => (
  <Select
    mode="tags"
    value={value}
    onChange={onChange}
    tokenSeparators={[",", ";"]}
    options={options}
    placeholder={placeholder}
    style={{ width: "100%" }}
    variant="borderless"
    open={options.length ? undefined : false}
    suffixIcon={null}
    tagRender={({ label, value: tagValue, closable, onClose }) => (
      <Tag
        color={isValidRecipient(tagValue) ? "default" : "error"}
        closable={closable}
        onClose={onClose}
        style={{ marginInlineEnd: 4 }}
      >
        {label}
      </Tag>
    )}
  />
);

const ComposeModal = ({ open, context, account, knownAddresses = [], onClose, onDone }) => {
  const { t } = useMailTranslation();
  const initial = useMemo(
    () => (open && account ? buildInitialState(context || {}, account, t) : null),
    [open, context, account]
  );
  const [state, setState] = useState(initial);
  const [files, setFiles] = useState([]);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [includeQuote, setIncludeQuote] = useState(true);
  const [busy, setBusy] = useState(null);
  const [bodyEdited, setBodyEdited] = useState(false);

  useEffect(() => {
    if (!initial) return;
    setState(initial);
    setFiles([]);
    setIncludeQuote(true);
    setBodyEdited(false);
    setShowCcBcc(Boolean(initial.cc.length || initial.bcc.length));
  }, [initial]);

  const addressOptions = useMemo(
    () =>
      knownAddresses.map(address => ({
        value: formatAddress(address),
        label: formatAddress(address)
      })),
    [knownAddresses]
  );

  if (!open || !state || !initial) return null;

  const update = patch => setState(prev => ({ ...prev, ...patch }));
  // Quill rewrites its initial HTML on mount, so only user-sourced edits count as changes.
  const isDirty =
    bodyEdited ||
    files.length > 0 ||
    JSON.stringify([state.to, state.cc, state.bcc, state.subject]) !==
      JSON.stringify([initial.to, initial.cc, initial.bcc, initial.subject]);

  const buildPayload = () => {
    const html = state.body + (includeQuote ? state.quotedHtml : "");
    return {
      to: state.to,
      cc: state.cc,
      bcc: state.bcc,
      subject: state.subject,
      html,
      text: htmlToText(html),
      inReplyTo: state.inReplyTo,
      references: state.references,
      answeredMessage: state.answeredMessage,
      draftUid: state.draftUid,
      includeAttachments:
        state.source && state.carried.length
          ? { ...state.source, indexes: state.carried.map(attachment => attachment.index) }
          : null
    };
  };

  const handleSend = async () => {
    const recipients = [...state.to, ...state.cc, ...state.bcc];
    if (!recipients.length) return Error(t("Add at least one recipient"));
    const invalid = recipients.find(recipient => !isValidRecipient(recipient));
    if (invalid) return Error(t("Invalid email address: {{address}}", { address: invalid }));
    if (!state.subject.trim()) {
      const confirmed = await new Promise(resolve =>
        Modal.confirm({
          title: t("Send without a subject?"),
          okText: t("Send"),
          cancelText: t("Cancel"),
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        })
      );
      if (!confirmed) return;
    }
    setBusy("send");
    try {
      const res = await mailApi.send(account._id, buildPayload(), files);
      Success(t("Message sent"));
      (res.data?.warnings || []).forEach(warning => Warning(t(warning)));
      onDone({ sent: true });
    } catch (error) {
      Error(getErrorMessage(error, t("Failed to send message")));
    } finally {
      setBusy(null);
    }
  };

  const handleSaveDraft = async () => {
    setBusy("draft");
    try {
      await mailApi.saveDraft(account._id, buildPayload(), files);
      Success(t("Draft saved"));
      onDone({ draft: true });
    } catch (error) {
      Error(getErrorMessage(error, t("Failed to save draft")));
    } finally {
      setBusy(null);
    }
  };

  const handleClose = () => {
    if (!isDirty || busy) return onClose();
    Modal.confirm({
      title: t("Save this message as a draft?"),
      okText: t("Save draft"),
      cancelText: t("Discard"),
      onOk: handleSaveDraft,
      onCancel: onClose
    });
  };

  const titles = {
    reply: t("Reply"),
    replyAll: t("Reply all"),
    forward: t("Forward"),
    draft: t("Edit draft")
  };

  return (
    <Modal
      open={open}
      title={titles[context?.mode] || t("New message")}
      onCancel={handleClose}
      maskClosable={false}
      width={900}
      className="mail-compose-modal"
      footer={
        <div className="mail-compose-footer">
          <Upload
            multiple
            fileList={files}
            beforeUpload={file => {
              setFiles(prev => [...prev, file]);
              return false;
            }}
            onRemove={file => setFiles(prev => prev.filter(item => item.uid !== file.uid))}
            showUploadList={false}
          >
            <Button icon={<PaperClipOutlined />}>{t("Attach files")}</Button>
          </Upload>
          <div className="mail-compose-footer-send">
            <Button onClick={handleSaveDraft} loading={busy === "draft"} disabled={busy === "send"}>
              {t("Save draft")}
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={busy === "send"}
              disabled={busy === "draft"}
            >
              {t("Send")}
            </Button>
          </div>
        </div>
      }
    >
      <div className="mail-compose-fields">
        <div className="mail-compose-row">
          <span className="mail-compose-label">{t("From")}</span>
          <span className="mail-compose-from">
            {formatAddress({ name: account.displayName, address: account.email })}
          </span>
        </div>
        <div className="mail-compose-row">
          <span className="mail-compose-label">{t("To")}</span>
          <RecipientSelect
            value={state.to}
            onChange={to => update({ to })}
            options={addressOptions}
          />
          {!showCcBcc && (
            <Button type="link" size="small" onClick={() => setShowCcBcc(true)}>
              {t("Cc/Bcc")}
            </Button>
          )}
        </div>
        {showCcBcc && (
          <>
            <div className="mail-compose-row">
              <span className="mail-compose-label">{t("Cc")}</span>
              <RecipientSelect
                value={state.cc}
                onChange={cc => update({ cc })}
                options={addressOptions}
              />
            </div>
            <div className="mail-compose-row">
              <span className="mail-compose-label">{t("Bcc")}</span>
              <RecipientSelect
                value={state.bcc}
                onChange={bcc => update({ bcc })}
                options={addressOptions}
              />
            </div>
          </>
        )}
        <div className="mail-compose-row">
          <span className="mail-compose-label">{t("Subject")}</span>
          <Input
            variant="borderless"
            aria-label={t("Subject")}
            value={state.subject}
            onChange={e => update({ subject: e.target.value })}
          />
        </div>
      </div>

      <ReactQuill
        theme="snow"
        value={state.body}
        onChange={(body, delta, source) => {
          update({ body });
          if (source === "user") setBodyEdited(true);
        }}
        modules={quillModules}
        className="mail-compose-editor"
      />

      {(state.carried.length > 0 || files.length > 0) && (
        <div className="mail-compose-attachments">
          {state.carried.map(attachment => (
            <Tag
              key={`carried-${attachment.index}`}
              icon={<PaperClipOutlined />}
              closable
              onClose={() =>
                update({ carried: state.carried.filter(item => item.index !== attachment.index) })
              }
            >
              {attachment.filename} ({formatSize(attachment.size)})
            </Tag>
          ))}
          {files.map(file => (
            <Tag
              key={file.uid}
              icon={<PaperClipOutlined />}
              closable
              onClose={() => setFiles(prev => prev.filter(item => item.uid !== file.uid))}
            >
              {file.name} ({formatSize(file.size)})
            </Tag>
          ))}
        </div>
      )}

      {state.quotedHtml && (
        <div className="mail-compose-quote">
          <Checkbox checked={includeQuote} onChange={e => setIncludeQuote(e.target.checked)}>
            {context?.mode === "forward"
              ? t("Include forwarded message")
              : t("Include original message")}
          </Checkbox>
          {includeQuote && <MessageBody html={state.quotedHtml} style={{ height: 220 }} />}
        </div>
      )}
    </Modal>
  );
};

export default ComposeModal;
